import React, { useEffect, useRef, useState } from 'react';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { InputSwitch } from 'primereact/inputswitch';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { Slider } from 'primereact/slider';
import { classNames } from 'primereact/utils';
import AwsImage from '../../../components/AwsImage';
import {
    AGENT_TYPES,
    INTELLIGENCE_LEVELS,
    INTELLIGENCE_OPTIONS,
    PROVIDERS,
    PROVIDER_OPTIONS,
    formatBytes,
    getAgentIntelligenceLevel,
    getTypeOptionsForProvider,
    isTypeSupportedByProvider
} from '../agentOptions';
import { addAgentFilesRequest, removeAgentFileRequest } from '../Services/request';
import { uploadFileGetUuid } from '../Services/upload';

const ACCEPTED_KNOWLEDGE_TYPES = '.pdf,.txt,.md,.docx,.doc,.csv,.json';
const MAX_TOTAL_BYTES = 30 * 1024 * 1024;
const MAX_AVATAR_BYTES = 10 * 1024 * 1024;

const schema = Yup.object().shape({
    name: Yup.string().max(80, 'Máximo de 80 caracteres').required('Campo obrigatório'),
    instructions: Yup.string().max(20000, 'Máximo de 20.000 caracteres').required('Campo obrigatório'),
    intelligenceLevel: Yup.string().required('Campo obrigatório'),
    provider: Yup.string().required('Campo obrigatório'),
    type: Yup.string().required('Campo obrigatório'),
    temperature: Yup.number().min(0.1).max(2).required('Campo obrigatório')
});

const sectionStyle = {
    padding: '1.25rem 1.75rem',
    borderBottom: '1px solid var(--surface-border)'
};

const labelStyle = {
    display: 'block',
    marginBottom: '0.375rem',
    fontSize: '0.875rem',
    fontWeight: 500
};

const inputStyle = { width: '100%', borderRadius: '8px' };

const FileRow = ({ name, size, status, disabled, onRemove }) => (
    <div className="flex align-items-center gap-3 p-2 border-1 surface-border border-round" style={{ background: 'var(--surface-ground)' }}>
        <i className={status && status !== 'ok' ? 'pi pi-spin pi-spinner' : 'pi pi-file'} />
        <div className="flex-1" style={{ minWidth: 0 }}>
            <div className="text-sm font-medium white-space-nowrap overflow-hidden text-overflow-ellipsis">{name}</div>
            <small className="text-color-secondary">{formatBytes(size)}{status && status !== 'ok' ? ` · ${status}` : ''}</small>
        </div>
        {onRemove && (
            <Button type="button" icon="pi pi-times" text rounded severity="secondary" disabled={disabled} onClick={onRemove} />
        )}
    </div>
);

const AgentFormPanel = ({ agent, onBack, onSaved, toast, createAgent, editAgent }) => {
    const avatarInputRef = useRef(null);
    const filesInputRef = useRef(null);
    const [avatarFile, setAvatarFile] = useState();
    const [avatarPreview, setAvatarPreview] = useState(agent?.aws?.url || null);
    const [existingFiles, setExistingFiles] = useState(agent?.agentFiles || []);
    const [pendingFiles, setPendingFiles] = useState([]);
    const [fileProgress, setFileProgress] = useState({});
    const [saving, setSaving] = useState(false);
    const [removingFileId, setRemovingFileId] = useState(null);
    const isEditing = Boolean(agent?.id);

    useEffect(() => {
        setAvatarFile(undefined);
        setAvatarPreview(agent?.aws?.url || null);
        setExistingFiles(agent?.agentFiles || []);
        setPendingFiles([]);
        setFileProgress({});
    }, [agent]);

    useEffect(() => () => {
        if (avatarPreview?.startsWith('blob:')) URL.revokeObjectURL(avatarPreview);
    }, [avatarPreview]);

    const totalBytes = existingFiles.reduce((total, file) => total + Number(file.size || 0), 0)
        + pendingFiles.reduce((total, file) => total + file.size, 0);
    const overLimit = totalBytes > MAX_TOTAL_BYTES;

    const selectAvatar = (event) => {
        const file = event.target.files?.[0];
        event.target.value = '';
        if (!file) return;
        if (!file.type.startsWith('image/')) {
            toast.current?.show({ severity: 'error', summary: 'Arquivo inválido', detail: 'Selecione uma imagem.' });
            return;
        }
        if (file.size > MAX_AVATAR_BYTES) {
            toast.current?.show({ severity: 'error', summary: 'Arquivo muito grande', detail: 'O avatar deve ter no máximo 10 MB.' });
            return;
        }
        setAvatarFile(file);
        setAvatarPreview(URL.createObjectURL(file));
    };

    const selectKnowledgeFiles = (event) => {
        const selected = Array.from(event.target.files || []);
        event.target.value = '';
        setPendingFiles((current) => {
            const existingNames = new Set([...existingFiles.map((file) => file.originalName), ...current.map((file) => file.name)]);
            return [...current, ...selected.filter((file) => !existingNames.has(file.name))];
        });
    };

    const uploadKnowledgeFiles = async () => {
        const refs = [];
        for (const file of pendingFiles) {
            setFileProgress((current) => ({ ...current, [file.name]: 'iniciando' }));
            const ref = await uploadFileGetUuid(file, (chunk, total) => {
                setFileProgress((current) => ({ ...current, [file.name]: `${chunk}/${total}` }));
            });
            refs.push(ref);
            setFileProgress((current) => ({ ...current, [file.name]: 'ok' }));
        }
        return refs;
    };

    const removeExistingFile = async (file) => {
        setRemovingFileId(file.id);
        try {
            await removeAgentFileRequest(agent.id, file.id);
            setExistingFiles((current) => current.filter((item) => item.id !== file.id));
            toast.current?.show({ severity: 'success', summary: 'Arquivo removido', life: 2000 });
        } catch (error) {
            toast.current?.show({ severity: 'error', summary: 'Erro', detail: error?.response?.data?.message || 'Não foi possível remover o arquivo.' });
        } finally {
            setRemovingFileId(null);
        }
    };

    const initialValues = {
        name: agent?.name || '',
        instructions: agent?.instructions || '',
        intelligenceLevel: getAgentIntelligenceLevel(agent),
        temperature: agent?.temperature ?? 0.7,
        config: agent?.config || {},
        isActive: agent?.isActive ?? true,
        provider: agent?.provider || PROVIDERS.OPENAI,
        type: agent?.type || AGENT_TYPES.TEXT
    };

    return (
        <Formik
            initialValues={initialValues}
            enableReinitialize
            validationSchema={schema}
            onSubmit={async (values) => {
                if (!isTypeSupportedByProvider(values.provider, values.type)) {
                    toast.current?.show({ severity: 'error', summary: 'Configuração incompatível', detail: 'A categoria escolhida não é suportada por este provider.' });
                    return;
                }
                if (values.provider !== PROVIDERS.OPENAI && existingFiles.length > 0) {
                    toast.current?.show({ severity: 'error', summary: 'Arquivos vinculados', detail: 'Remova os arquivos de conhecimento antes de trocar o provider.' });
                    return;
                }

                setSaving(true);
                try {
                    const avatarRef = avatarFile ? await uploadFileGetUuid(avatarFile) : null;
                    const fileRefs = pendingFiles.length ? await uploadKnowledgeFiles() : [];
                    const body = {
                        ...values,
                        ...(avatarRef || {}),
                        ...(!isEditing && fileRefs.length ? { files: fileRefs } : {})
                    };
                    const saved = isEditing
                        ? await editAgent({ ...body, id: agent.id })
                        : await createAgent(body);

                    let addedFiles = [];
                    if (isEditing && fileRefs.length) {
                        addedFiles = await addAgentFilesRequest(agent.id, fileRefs);
                    }
                    const completeAgent = {
                        ...(agent || {}),
                        ...(saved || {}),
                        ...values,
                        agentFiles: isEditing
                            ? [...existingFiles, ...addedFiles]
                            : (saved?.agentFiles || [])
                    };
                    setPendingFiles([]);
                    setFileProgress({});
                    setExistingFiles(completeAgent.agentFiles);
                    onSaved(completeAgent);
                    toast.current?.show({ severity: 'success', summary: 'Sucesso', detail: isEditing ? 'Assistente atualizado.' : 'Assistente criado.', life: 2200 });
                } catch (error) {
                    toast.current?.show({ severity: 'error', summary: 'Erro ao salvar', detail: error?.response?.data?.message || 'Não foi possível salvar o assistente.', life: 4000 });
                } finally {
                    setSaving(false);
                }
            }}
        >
            {({ values, errors, touched, handleChange, setFieldValue, submitForm }) => (
                <Form style={{ minHeight: '100%' }}>
                    <div className="flex align-items-center justify-content-between px-4 py-3 border-bottom-1 surface-border sticky top-0 surface-card" style={{ zIndex: 2 }}>
                        <div className="flex align-items-center gap-3">
                            <Button type="button" icon="pi pi-arrow-left" text rounded severity="secondary" onClick={onBack} />
                            <span className="font-semibold">{isEditing ? 'Editar Assistente' : 'Criar Assistente'}</span>
                        </div>
                        <Button type="button" label={saving ? 'Salvando...' : isEditing ? 'Salvar' : 'Criar Assistente'} loading={saving} disabled={saving || overLimit} onClick={submitForm} />
                    </div>

                    <div style={sectionStyle}>
                        <label style={labelStyle}>Imagem</label>
                        <div className="flex align-items-center gap-4">
                            <button type="button" onClick={() => avatarInputRef.current?.click()} className="border-1 surface-border border-round-xl overflow-hidden p-0" style={{ width: 80, height: 80, background: 'var(--surface-ground)', cursor: 'pointer' }}>
                                {avatarFile && avatarPreview ? (
                                    <img src={avatarPreview} alt="Prévia do avatar" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                ) : agent?.aws_fk || agent?.aws?.id || agent?.aws?.url ? (
                                    <AwsImage
                                        awsId={agent?.aws_fk ?? agent?.aws?.id}
                                        aws={agent?.aws}
                                        fallbackUrl={avatarPreview}
                                        usage="avatar"
                                        width={80}
                                        height={80}
                                        alt={values.name}
                                        fallback={<i className="pi pi-plus text-color-secondary" />}
                                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                    />
                                ) : (
                                    <i className="pi pi-plus text-color-secondary" />
                                )}
                            </button>
                            <div>
                                <Button type="button" label={agent?.aws_fk || avatarFile ? 'Trocar imagem' : 'Selecionar imagem'} icon="pi pi-upload" outlined size="small" onClick={() => avatarInputRef.current?.click()} />
                                <div className="text-xs text-color-secondary mt-2">PNG ou JPG, até 10 MB</div>
                            </div>
                            <input ref={avatarInputRef} type="file" accept="image/*" hidden onChange={selectAvatar} />
                        </div>
                    </div>

                    <div style={sectionStyle}>
                        <div className="flex justify-content-between">
                            <label style={labelStyle}>Nome do Assistente *</label>
                            <small className="text-color-secondary">{values.name.length} / 80</small>
                        </div>
                        <InputText name="name" value={values.name} maxLength={80} onChange={handleChange} placeholder="Digite o nome do assistente" style={inputStyle} className={classNames({ 'p-invalid': touched.name && errors.name })} />
                        {touched.name && errors.name && <small className="p-error">{errors.name}</small>}
                    </div>

                    <div style={sectionStyle}>
                        <div className="flex justify-content-between">
                            <label style={labelStyle}>Instruções *</label>
                            <small className="text-color-secondary">{values.instructions.length} / 20000</small>
                        </div>
                        <InputTextarea name="instructions" value={values.instructions} maxLength={20000} rows={7} onChange={handleChange} placeholder="Digite as instruções para o assistente (System Prompt)" style={{ ...inputStyle, resize: 'vertical' }} className={classNames({ 'p-invalid': touched.instructions && errors.instructions })} />
                        {touched.instructions && errors.instructions && <small className="p-error">{errors.instructions}</small>}
                    </div>

                    <div style={sectionStyle}>
                        <label style={labelStyle}>Inteligência Artificial</label>
                        <Dropdown value={values.provider} options={PROVIDER_OPTIONS} onChange={(event) => {
                            const provider = event.value;
                            setFieldValue('provider', provider);
                            if (provider === PROVIDERS.PERPLEXITY && values.intelligenceLevel === INTELLIGENCE_LEVELS.HIGH) setFieldValue('intelligenceLevel', INTELLIGENCE_LEVELS.MEDIUM);
                            const supportedTypes = getTypeOptionsForProvider(provider);
                            if (!supportedTypes.some((option) => option.value === values.type)) setFieldValue('type', supportedTypes[0]?.value || '');
                            if (provider !== PROVIDERS.OPENAI) setPendingFiles([]);
                        }} style={inputStyle} />

                        {values.type !== AGENT_TYPES.IMAGE && (
                            <>
                                <div className="text-sm font-medium mt-4 mb-1">Perfil de resposta</div>
                                <div className="text-sm text-color-secondary mb-3">A Ludz seleciona automaticamente o modelo adequado.</div>
                                <div className="grid">
                                    {INTELLIGENCE_OPTIONS.map((option) => {
                                        const disabled = values.provider === PROVIDERS.PERPLEXITY && option.value === INTELLIGENCE_LEVELS.HIGH;
                                        const active = values.intelligenceLevel === option.value;
                                        return (
                                            <div className="col-12 md:col-4" key={option.value}>
                                                <button
                                                    type="button"
                                                    disabled={disabled}
                                                    onClick={() => setFieldValue('intelligenceLevel', option.value)}
                                                    className="w-full h-full text-left p-3 border-1 border-round-lg"
                                                    style={{ minHeight: 120, cursor: disabled ? 'not-allowed' : 'pointer', borderColor: active ? 'var(--primary-color)' : 'var(--surface-border)', background: active ? 'var(--primary-50)' : 'var(--surface-card)', opacity: disabled ? 0.5 : 1 }}
                                                >
                                                    <i className={`${option.icon} ${active ? 'text-primary' : 'text-color-secondary'}`} />
                                                    <div className="font-semibold mt-2">{option.label}</div>
                                                    <div className="text-xs text-color-secondary mt-2">{disabled ? 'Indisponível para busca web.' : option.description}</div>
                                                    {option.recommended && !disabled && <div className="text-xs text-primary font-semibold mt-2">RECOMENDADO</div>}
                                                </button>
                                            </div>
                                        );
                                    })}
                                </div>
                            </>
                        )}
                    </div>

                    <div style={sectionStyle}>
                        <label style={labelStyle}>Categoria *</label>
                        <Dropdown value={values.type} options={getTypeOptionsForProvider(values.provider)} onChange={(event) => setFieldValue('type', event.value)} style={inputStyle} />
                    </div>

                    <div style={sectionStyle}>
                        <div className="flex justify-content-between mb-3">
                            <span className="text-sm font-medium">Precisão e Criatividade <span className="font-normal text-color-secondary">(avançado)</span></span>
                            <span className="text-primary font-semibold">{Number(values.temperature).toFixed(1)}</span>
                        </div>
                        <Slider value={values.temperature * 10} min={1} max={20} step={1} onChange={(event) => setFieldValue('temperature', Math.round(Number(event.value)) / 10)} />
                        <div className="flex justify-content-between text-xs text-color-secondary mt-2"><span>0.1 · Mais preciso</span><span>Mais criativo · 2.0</span></div>
                    </div>

                    <div style={sectionStyle} className="flex align-items-center justify-content-between">
                        <div><div className="text-sm font-medium">Status</div><div className="text-sm text-color-secondary mt-1">{values.isActive ? 'Assistente ativo e disponível' : 'Assistente inativo'}</div></div>
                        <InputSwitch checked={values.isActive} onChange={(event) => setFieldValue('isActive', event.value)} />
                    </div>

                    <div style={{ ...sectionStyle, borderBottom: 0 }}>
                        <div className="flex align-items-center justify-content-between mb-3">
                            <div><div className="text-sm font-medium">Arquivos de conhecimento</div><div className="text-xs text-color-secondary mt-1">PDF, TXT, DOCX, CSV, MD ou JSON · máximo de 30 MB</div></div>
                            <Button type="button" label="Adicionar" icon="pi pi-plus" outlined size="small" disabled={values.provider !== PROVIDERS.OPENAI || saving} onClick={() => filesInputRef.current?.click()} />
                        </div>
                        {values.provider !== PROVIDERS.OPENAI && <div className="p-3 border-1 surface-border border-round text-sm text-color-secondary"><i className="pi pi-lock mr-2" />Arquivos estão disponíveis apenas para OpenAI.{existingFiles.length > 0 && ' Remova os arquivos existentes antes de salvar.'}</div>}
                        {(existingFiles.length > 0 || pendingFiles.length > 0) && <div className={overLimit ? 'text-red-500 text-sm mb-2' : 'text-color-secondary text-sm mb-2'}>{formatBytes(totalBytes)} de 30 MB usados</div>}
                        <div className="flex flex-column gap-2">
                            {existingFiles.map((file) => <FileRow key={file.id} name={file.originalName} size={file.size} disabled={saving || removingFileId !== null} onRemove={() => removeExistingFile(file)} />)}
                            {pendingFiles.map((file, index) => <FileRow key={`${file.name}-${index}`} name={file.name} size={file.size} status={fileProgress[file.name]} disabled={saving} onRemove={() => setPendingFiles((current) => current.filter((_, itemIndex) => itemIndex !== index))} />)}
                        </div>
                        {values.provider === PROVIDERS.OPENAI && existingFiles.length === 0 && pendingFiles.length === 0 && <button type="button" className="w-full p-4 border-2 border-dashed surface-border border-round-lg surface-card text-color-secondary" style={{ cursor: 'pointer' }} onClick={() => filesInputRef.current?.click()}><i className="pi pi-cloud-upload mr-2" />Clique para selecionar arquivos</button>}
                        {overLimit && <small className="p-error">O total de arquivos ultrapassa 30 MB.</small>}
                        <input ref={filesInputRef} type="file" multiple accept={ACCEPTED_KNOWLEDGE_TYPES} hidden onChange={selectKnowledgeFiles} />
                    </div>
                </Form>
            )}
        </Formik>
    );
};

export default AgentFormPanel;
